import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  EmptyPlaceholderComponent,
  ModalComponent,
  TagComponent,
  TagVariant,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { LazyLoadEvent } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { finalize } from 'rxjs';
import { CreditsAdapter } from '../../adapters/credits.adapter';
import { CreditsActionType } from '../../enums/credits-action-type.enum';
import { CreditsLogEntry } from '../../interfaces/credits-log';
import { FontawesomeService } from '../../services/fontawesome.service';
import { CREDITS_METER_COLORS } from '../credits-counter/credits-meter.util';
import { ViewLoaderComponent } from '../view-loader/view-loader.component';

@Component({
  selector: 'app-credits-log-modal',
  templateUrl: './credits-log-modal.component.html',
  styleUrl: './credits-log-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    TranslocoPipe,
    TableModule,
    EmptyPlaceholderComponent,
    ModalComponent,
    TagComponent,
    ViewLoaderComponent,
    FaIconComponent,
  ],
})
export class CreditsLogModalComponent {
  private readonly creditsAdapter = inject(CreditsAdapter);
  private readonly toastsService = inject(ToastsService);
  private readonly modal = viewChild.required<ModalComponent>('modal');
  readonly fontawesomeService = inject(FontawesomeService);

  readonly entries = signal<CreditsLogEntry[]>([]);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  readonly offset = signal(0);
  readonly recordsPerPage = 100;

  /**
   * Opens the credit log modal and loads the first page.
   *
   * @returns {void}
   */
  open(): void {
    this.offset.set(0);
    this.entries.set([]);
    this.totalRecords.set(0);
    this.loadPage(0);
    this.modal().open();
  }

  /**
   * Loads a page of credit log entries when the table paginator changes.
   *
   * @param {LazyLoadEvent} event PrimeNG lazy-load event.
   * @returns {void}
   */
  onLoadEvent(event: LazyLoadEvent): void {
    const offset = event.first ?? 0;
    if (offset === this.offset()) {
      return;
    }
    this.loadPage(offset);
  }

  /**
   * Whether the log row increases available credits.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {boolean} True for add, reset and reservation release.
   */
  isCreditIncrease(entry: CreditsLogEntry): boolean {
    return (
      entry.actionType === CreditsActionType.ADD ||
      entry.actionType === CreditsActionType.RESET ||
      entry.actionType === CreditsActionType.RELEASE_RESERVATION
    );
  }

  /**
   * Whether the row is a monthly credit reset.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {boolean} True for RESET actions.
   */
  isReset(entry: CreditsLogEntry): boolean {
    return entry.actionType === CreditsActionType.RESET;
  }

  /**
   * Whether the consume uses credits that were reserved earlier.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {boolean} True when a reservation is settled.
   */
  isReservedConsume(entry: CreditsLogEntry): boolean {
    return entry.settlesReservation;
  }

  /**
   * Tag color aligned with the credits counter: reserved yellow, used gray,
   * refill blue.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {TagVariant} Tag color variant.
   */
  amountTagVariant(entry: CreditsLogEntry): TagVariant {
    if (entry.actionType === CreditsActionType.CONSUME) {
      return TagVariant.GRAY;
    }
    if (entry.actionType === CreditsActionType.RESERVE) {
      return TagVariant.YELLOW;
    }
    return TagVariant.DEFAULT;
  }

  /**
   * Blue accent for inclusive and extra refills. Consume and reserve use variants.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {string | null} Hex color for the tag accent, or null.
   */
  amountTagAccent(entry: CreditsLogEntry): string | null {
    if (
      entry.actionType === CreditsActionType.CONSUME ||
      entry.actionType === CreditsActionType.RESERVE
    ) {
      return null;
    }
    return CREDITS_METER_COLORS.availableExtra;
  }

  /**
   * Signed amount label for a log row. Reset shows the target balance without a plus.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {string} Amount with + or - prefix, or the reset target value.
   */
  signedAmount(entry: CreditsLogEntry): string {
    if (this.isReset(entry)) {
      return String(entry.amount);
    }
    return this.isCreditIncrease(entry)
      ? `+${entry.amount}`
      : `-${entry.amount}`;
  }

  /**
   * Display name for a log row, or empty when the action was system-triggered.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {string | null} Full name or null for system actions.
   */
  userDisplayName(entry: CreditsLogEntry): string | null {
    const firstName = entry.userFirstName?.trim() ?? '';
    const lastName = entry.userLastName?.trim() ?? '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName.length > 0 ? fullName : null;
  }

  /**
   * Display name for the related application, if the log row has one.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {string | null} Applicant name or null when none is linked.
   */
  applicationDisplayName(entry: CreditsLogEntry): string | null {
    const firstName = entry.applicationFirstName?.trim() ?? '';
    const lastName = entry.applicationLastName?.trim() ?? '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName.length > 0 ? fullName : null;
  }

  /**
   * i18n key for a credit log action type.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {string} Transloco key.
   */
  actionLabelKey(entry: CreditsLogEntry): string {
    if (this.isReservedConsume(entry)) {
      return 'company-tool.views.settings.company.payment.credits-log.actions.CONSUME_RESERVED';
    }
    return `company-tool.views.settings.company.payment.credits-log.actions.${entry.actionType}`;
  }

  /**
   * i18n key for a credit type.
   *
   * @param {CreditsLogEntry} entry Credit log row.
   * @returns {string} Transloco key.
   */
  typeLabelKey(entry: CreditsLogEntry): string {
    return `company-tool.views.settings.company.payment.credits-log.types.${entry.creditType}`;
  }

  /**
   * Fetches one page of credit log entries.
   *
   * @param {number} offset Number of rows to skip.
   * @returns {void}
   */
  private loadPage(offset: number): void {
    this.offset.set(offset);
    this.loading.set(true);

    this.creditsAdapter
      .getCreditsLog(offset, this.recordsPerPage)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.entries.set(response.results ?? []);
          this.totalRecords.set(response.count ?? 0);
        },
        error: () => {
          this.entries.set([]);
          this.totalRecords.set(0);
          this.toastsService.alert(
            'company-tool.views.settings.company.payment.credits-log.load-error',
          );
        },
      });
  }
}
